import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { theme } from '../../../styles/theme';
import { useCurrency } from '../../../lib/currency';
import { useAuth } from '../../../lib/auth';
import { AdminContainer } from '../AdminShell';

type QuoteRow = {
  id: string;
  quote_number: string;
  customer_id: string | null;
  kind: 'to_nuled' | 'to_client';
  status: string;
  total: number | null;
  currency: string;
  valid_until: string | null;
  sent_at: string | null;
  created_at: string;
  notes: string | null;
  client_name: string | null;
  customers?: { business_name: string; tier_discount_pct: number | null; tier_name: string | null } | null;
};

type Customer = {
  id: string;
  business_name: string;
  tier_discount_pct: number | null;
  tier_name: string | null;
};

type ProductOption = {
  id: string;
  sku: string;             // SKU canonical (puede tener placeholders XXXX)
  name: string;
  brand_code: string;
  msrp_usd: number | null;
  unit: string | null;
};

// Opción para mostrar en search: producto OR variante
type SearchOption = {
  product_id: string;
  variant_id: string | null;     // null si es el canonical sin variantes
  sku: string;                   // SKU real (sin XXXX)
  name: string;
  brand_code: string;
  msrp_usd: number | null;
  unit: string | null;
  variant_label: string | null;  // ej "Negro · 2700K · 24°"
};

type VariantRow = {
  id: string;
  product_id: string;
  full_sku: string;
  finish: string | null;
  color_temperature_k: number | null;
  beam_angle_deg: number | null;
  wattage_w: number | null;
  msrp_usd: number | null;
};

type LineItem = {
  product_id: string;
  variant_id: string | null;
  sku: string;
  brand_code: string;
  description: string;
  quantity: number;
  unit: string;
  unit_price: number; // USD
  discount_pct: number;
};

type Tab = 'all' | 'draft' | 'sent' | 'paid' | 'lost';

export function AdminCotizaciones() {
  const [tab, setTab] = useState<Tab>('all');
  const [quotes, setQuotes] = useState<QuoteRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [showBuilder, setShowBuilder] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [editId, setEditId] = useState<string | null>(null);

  useEffect(() => { reload(); }, []);

  async function reload() {
    setLoading(true);
    const { data } = await supabase
      .from('quotes')
      .select('id, quote_number, customer_id, kind, status, total, currency, valid_until, sent_at, created_at, notes, client_name, customers(business_name, tier:customer_tiers(name, default_discount_pct))')
      .order('created_at', { ascending: false })
      .limit(200);
    const rows = ((data as any[]) ?? []).map(r => ({
      ...r,
      customers: r.customers ? {
        business_name: r.customers.business_name,
        tier_discount_pct: r.customers.tier?.default_discount_pct ?? null,
        tier_name: r.customers.tier?.name ?? null,
      } : null,
    })) as QuoteRow[];
    setQuotes(rows);
    setLoading(false);
  }

  async function makeNewVersion(quoteId: string) {
    const { data, error } = await supabase.rpc('new_quote_version', { p_quote_id: quoteId });
    if (error) { alert(error.message); return; }
    setDetailId(null);
    setEditId(data as string);
    reload();
  }

  const counts = useMemo(() => ({
    all: quotes.length,
    draft: quotes.filter(q => q.status === 'draft').length,
    sent: quotes.filter(q => ['sent', 'viewed'].includes(q.status)).length,
    paid: quotes.filter(q => q.status === 'paid').length,
    lost: quotes.filter(q => ['lost', 'rejected', 'expired'].includes(q.status)).length,
  }), [quotes]);

  const filtered = useMemo(() => {
    return quotes.filter(q => {
      if (tab === 'all') return true;
      if (tab === 'sent') return ['sent', 'viewed'].includes(q.status);
      if (tab === 'lost') return ['lost', 'rejected', 'expired'].includes(q.status);
      return q.status === tab;
    });
  }, [quotes, tab]);

  return (
    <AdminContainer
      title="Cotizaciones"
      eyebrow={`${quotes.length} totales`}
      action={
        <button style={btnPrimary} onClick={() => setShowBuilder(true)}>
          + Nueva cotización
        </button>
      }
    >
      <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderBottom: `1px solid ${theme.border}` }}>
        <TabBtn active={tab === 'all'} onClick={() => setTab('all')}>Todas <Mini>{counts.all}</Mini></TabBtn>
        <TabBtn active={tab === 'draft'} onClick={() => setTab('draft')}>Borradores <Mini>{counts.draft}</Mini></TabBtn>
        <TabBtn active={tab === 'sent'} onClick={() => setTab('sent')}>Enviadas <Mini>{counts.sent}</Mini></TabBtn>
        <TabBtn active={tab === 'paid'} onClick={() => setTab('paid')}>Pagadas <Mini>{counts.paid}</Mini></TabBtn>
        <TabBtn active={tab === 'lost'} onClick={() => setTab('lost')}>Perdidas <Mini>{counts.lost}</Mini></TabBtn>
      </div>

      <div style={{ marginBottom: 8, fontSize: 12, color: theme.textMuted }}>
        {loading ? 'Cargando…' : `${filtered.length} de ${quotes.length}`}
      </div>

      <div style={{ background: theme.bg, border: `1px solid ${theme.border}`, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 800 }}>
          <thead>
            <tr style={{ background: theme.bgSoft }}>
              <Th>#</Th>
              <Th>Cliente / Para</Th>
              <Th>Tipo</Th>
              <Th>Status</Th>
              <Th style={{ textAlign: 'right' }}>Total</Th>
              <Th>Vence</Th>
              <Th>Creada</Th>
              <Th style={{ textAlign: 'center', width: 110 }}>PDF</Th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(q => (
              <tr key={q.id} style={{ borderBottom: `1px solid ${theme.border}`, cursor: 'pointer' }}
                  onClick={() => setDetailId(q.id)}>
                <Td><span style={{ fontFamily: theme.font.mono, fontSize: 11, color: theme.textMuted }}>
                  {q.quote_number}
                </span></Td>
                <Td>
                  <div style={{ fontWeight: 600, color: theme.brand }}>
                    {q.customers?.business_name ?? q.client_name ?? '—'}
                  </div>
                  {q.customers?.tier_name && (
                    <div style={{ fontSize: 10, color: theme.textMuted, marginTop: 2 }}>
                      {q.customers.tier_name} · −{q.customers.tier_discount_pct}%
                    </div>
                  )}
                </Td>
                <Td>
                  <Tag color={q.kind === 'to_nuled' ? theme.accentDark : theme.brandTag.lutron}>
                    {q.kind === 'to_nuled' ? 'A NULED' : 'AL CLIENTE'}
                  </Tag>
                </Td>
                <Td><StatusTag status={q.status} /></Td>
                <Td style={{ textAlign: 'right', fontWeight: 700 }}>
                  {q.total != null ? `$${Number(q.total).toLocaleString('en-US', { maximumFractionDigits: 2 })} ${q.currency}` : '—'}
                </Td>
                <Td>{q.valid_until ?? '—'}</Td>
                <Td style={{ fontSize: 11, color: theme.textMuted }}>
                  {new Date(q.created_at).toLocaleDateString('es-MX')}
                </Td>
                <Td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                  <a
                    href={`/cotizacion/${q.id}/pdf`}
                    target="_blank" rel="noreferrer"
                    onClick={e => e.stopPropagation()}
                    title="PDF para cliente (sin margen)"
                    style={pdfBtnStyle}
                  >
                    📄
                  </a>
                  <a
                    href={`/cotizacion/${q.id}/pdf?modo=interno`}
                    target="_blank" rel="noreferrer"
                    onClick={e => e.stopPropagation()}
                    title="PDF interno (con margen)"
                    style={{ ...pdfBtnStyle, marginLeft: 4 }}
                  >
                    📋
                  </a>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>

        {filtered.length === 0 && !loading && (
          <div style={{ padding: 40, textAlign: 'center', color: theme.textMuted, fontSize: 13 }}>
            No hay cotizaciones en este filtro.{' '}
            <button onClick={() => setShowBuilder(true)} style={{
              background: 'transparent', border: 'none',
              color: theme.accentDark, fontWeight: 700, cursor: 'pointer',
              fontFamily: theme.font.sans, textDecoration: 'underline',
            }}>
              Crear la primera
            </button>
          </div>
        )}
      </div>

      {(showBuilder || editId) && (
        <QuoteBuilder
          editQuoteId={editId ?? undefined}
          key={editId ?? 'new'}
          onClose={() => { setShowBuilder(false); setEditId(null); }}
          onSaved={() => { setShowBuilder(false); setEditId(null); reload(); }}
        />
      )}

      {detailId && (
        <QuoteDetail
          quoteId={detailId}
          onClose={() => setDetailId(null)}
          onChanged={() => { reload(); }}
          onEdit={(id) => { setDetailId(null); setEditId(id); }}
          onNewVersion={makeNewVersion}
          onOpenVersion={(id) => setDetailId(id)}
        />
      )}
    </AdminContainer>
  );
}

// =================== BUILDER ===================

function QuoteBuilder({ onClose, onSaved, editQuoteId }: { onClose: () => void; onSaved: () => void; editQuoteId?: string }) {
  const { user } = useAuth();
  const { fmtUsd, usdMxn } = useCurrency();

  const [step, setStep] = useState<'cliente' | 'items' | 'resumen'>('cliente');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [matches, setMatches] = useState<SearchOption[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [kind, setKind] = useState<'to_nuled' | 'to_client'>('to_nuled');
  const [markupPct, setMarkupPct] = useState(35);
  const [items, setItems] = useState<LineItem[]>([]);
  const [productSearch, setProductSearch] = useState('');
  const [validUntil, setValidUntil] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 14);
    return d.toISOString().slice(0, 10);
  });
  const [notes, setNotes] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('Contado · transferencia');
  const [deliveryTerms, setDeliveryTerms] = useState('FOB CDMX · 2-3 semanas');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    supabase.from('customers')
      .select('id, business_name, tier:customer_tiers(name, default_discount_pct)')
      .eq('status', 'active')
      .order('business_name')
      .then(({ data }) => {
        const cs = ((data as any[]) ?? []).map(c => ({
          id: c.id,
          business_name: c.business_name,
          tier_name: c.tier?.name ?? null,
          tier_discount_pct: c.tier?.default_discount_pct ?? null,
        })) as Customer[];
        setCustomers(cs);
      });
  }, []);

  // Modo edición: cargar cotización existente
  useEffect(() => {
    if (!editQuoteId) return;
    (async () => {
      const [{ data: q }, { data: its }, ] = await Promise.all([
        supabase.from('quotes').select('*, customers(business_name, tier:customer_tiers(name, default_discount_pct))').eq('id', editQuoteId).maybeSingle(),
        supabase.from('quote_items').select('*').eq('quote_id', editQuoteId).order('sort_order'),
      ]);
      if (q) {
        const qq = q as any;
        setSelectedCustomer({
          id: qq.customer_id,
          business_name: qq.customers?.business_name ?? '—',
          tier_name: qq.customers?.tier?.name ?? null,
          tier_discount_pct: qq.customers?.tier?.default_discount_pct ?? null,
        });
        setKind(qq.kind === 'to_client' ? 'to_client' : 'to_nuled');
        if (qq.markup_pct != null) setMarkupPct(Number(qq.markup_pct));
        if (qq.valid_until) setValidUntil(String(qq.valid_until).slice(0, 10));
        setNotes(qq.notes ?? '');
        if (qq.payment_terms) setPaymentTerms(qq.payment_terms);
        if (qq.delivery_terms) setDeliveryTerms(qq.delivery_terms);
      }
      setItems(((its as any[]) ?? []).map(it => ({
        product_id: it.product_id, variant_id: it.variant_id, sku: it.sku,
        brand_code: it.brand_code, description: it.description, quantity: Number(it.quantity),
        unit: it.unit, unit_price: Number(it.unit_price), discount_pct: Number(it.discount_pct),
      })));
      setStep('items');
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editQuoteId]);

  // Búsqueda de productos server-side, bajo demanda (evita precargar miles de filas).
  useEffect(() => {
    const q = productSearch.trim();
    if (q.length < 2) { setMatches([]); return; }
    let alive = true;
    const t = setTimeout(async () => {
      const like = `%${q}%`;
      const { data: prods } = await supabase.from('products_public')
        .select('id, sku, brand_code, name, msrp_usd, unit')
        .eq('status', 'active')
        .or(`sku.ilike.${like},name.ilike.${like}`)
        .limit(20);
      const plist = (prods as ProductOption[]) ?? [];
      const ids = plist.map(p => p.id);
      let vlist: VariantRow[] = [];
      if (ids.length > 0) {
        const { data: vs } = await supabase.from('product_variants')
          .select('id, product_id, full_sku, finish, color_temperature_k, beam_angle_deg, wattage_w, msrp_usd')
          .in('product_id', ids).eq('active', true).limit(400);
        vlist = (vs as VariantRow[]) ?? [];
      }
      const byProd: Record<string, VariantRow[]> = {};
      for (const v of vlist) (byProd[v.product_id] ||= []).push(v);
      const opts: SearchOption[] = [];
      for (const p of plist) {
        const vs = byProd[p.id] ?? [];
        if (vs.length === 0) {
          opts.push({ product_id: p.id, variant_id: null, sku: p.sku, name: p.name, brand_code: p.brand_code, msrp_usd: p.msrp_usd, unit: p.unit, variant_label: null });
        } else {
          for (const v of vs) {
            const parts: string[] = [];
            if (v.finish) parts.push(v.finish);
            if (v.color_temperature_k) parts.push(`${v.color_temperature_k}K`);
            if (v.beam_angle_deg) parts.push(`${v.beam_angle_deg}°`);
            if (v.wattage_w) parts.push(`${v.wattage_w}W`);
            const label = parts.join(' · ');
            opts.push({ product_id: p.id, variant_id: v.id, sku: v.full_sku, name: p.name, brand_code: p.brand_code, msrp_usd: v.msrp_usd ?? p.msrp_usd, unit: p.unit, variant_label: label || null });
          }
        }
      }
      if (alive) setMatches(opts.slice(0, 30));
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [productSearch]);

  const tierDiscount = selectedCustomer?.tier_discount_pct ?? 0;

  const subtotal = items.reduce((s, it) => s + it.unit_price * it.quantity * (1 - it.discount_pct / 100), 0);
  const total = subtotal; // tax / shipping se agregan después

  function addProduct(p: SearchOption) {
    const pricePerUnit = p.msrp_usd ?? 0;
    // Si kind=to_nuled (mayoreo): aplicar tier_discount como discount_pct por línea
    // Si kind=to_client: precio al MSRP, el distribuidor le cotiza al cliente final
    const linePct = kind === 'to_nuled' ? tierDiscount : 0;
    const description = p.variant_label ? `${p.name} · ${p.variant_label}` : p.name;
    setItems(prev => [...prev, {
      product_id: p.product_id,
      variant_id: p.variant_id,
      sku: p.sku,
      brand_code: p.brand_code,
      description,
      quantity: 1,
      unit: p.unit ?? 'pieza',
      unit_price: pricePerUnit,
      discount_pct: linePct,
    }]);
    setProductSearch('');
  }

  function updateItem(idx: number, patch: Partial<LineItem>) {
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, ...patch } : it));
  }

  function removeItem(idx: number) {
    setItems(prev => prev.filter((_, i) => i !== idx));
  }

  async function save(action: 'draft' | 'send') {
    if (!selectedCustomer) { setErr('Selecciona un cliente'); return; }
    if (items.length === 0) { setErr('Agrega al menos un producto'); return; }
    setSaving(true);
    setErr(null);

    const status = action === 'send' ? 'sent' : 'draft';
    const sentAt = action === 'send' ? new Date().toISOString() : null;

    const buildItems = (quoteId: string) => items.map((it, i) => ({
      quote_id: quoteId,
      product_id: it.product_id,
      variant_id: it.variant_id,
      sku: it.sku,
      brand_code: it.brand_code,
      description: it.description,
      quantity: it.quantity,
      unit: it.unit,
      unit_price: it.unit_price,
      discount_pct: it.discount_pct,
      line_total: it.unit_price * it.quantity * (1 - it.discount_pct / 100),
      sort_order: i,
    }));

    if (editQuoteId) {
      // ----- Editar cotización existente (misma versión) -----
      const patch: any = {
        customer_id: selectedCustomer.id,
        kind,
        status,
        valid_until: validUntil,
        payment_terms: paymentTerms,
        delivery_terms: deliveryTerms,
        tipo_cambio_cierre: null,
        notes,
        markup_pct: kind === 'to_client' ? markupPct : null,
        subtotal,
        total,
      };
      if (action === 'send') patch.sent_at = sentAt;
      const { error: uErr } = await supabase.from('quotes').update(patch).eq('id', editQuoteId);
      if (uErr) { setErr(uErr.message); setSaving(false); return; }
      const { error: dErr } = await supabase.from('quote_items').delete().eq('quote_id', editQuoteId);
      if (dErr) { setErr(dErr.message); setSaving(false); return; }
      const { error: itErr } = await supabase.from('quote_items').insert(buildItems(editQuoteId));
      if (itErr) { setErr(itErr.message); setSaving(false); return; }
      setSaving(false);
      onSaved();
      return;
    }

    const quoteNumber = `Q-${Date.now().toString().slice(-8)}`;
    const { data: qData, error: qErr } = await supabase.from('quotes').insert({
      quote_number: quoteNumber,
      customer_id: selectedCustomer.id,
      kind,
      currency: 'USD',
      status,
      sent_at: sentAt,
      valid_until: validUntil,
      payment_terms: paymentTerms,
      delivery_terms: deliveryTerms,
      tipo_cambio_cierre: null,
      notes,
      markup_pct: kind === 'to_client' ? markupPct : null,
      subtotal,
      total,
      tax_rate: 0,
      tax_amount: 0,
      shipping_amount: 0,
      discount_amount: 0,
      created_by: user?.id ?? null,
    }).select().single();

    if (qErr || !qData) { setErr(qErr?.message ?? 'Error creando cotización'); setSaving(false); return; }

    const { error: itErr } = await supabase.from('quote_items').insert(buildItems(qData.id));
    if (itErr) { setErr(itErr.message); setSaving(false); return; }

    setSaving(false);
    onSaved();
  }

  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: 'rgba(0,0,0,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: 16,
    }}>
      <div style={{
        background: theme.bg,
        border: `1px solid ${theme.border}`,
        width: '100%', maxWidth: 1100,
        maxHeight: '94vh', display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{ padding: '20px 28px', borderBottom: `1px solid ${theme.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 11, color: theme.accentDark, letterSpacing: 1.5, textTransform: 'uppercase', fontWeight: 700 }}>
              {editQuoteId ? 'Editar cotización' : 'Nueva cotización'} · paso {step === 'cliente' ? '1' : step === 'items' ? '2' : '3'} de 3
            </div>
            <h2 style={{ fontFamily: theme.font.head, fontSize: 22, fontWeight: 400, color: theme.brand, margin: '4px 0 0' }}>
              {step === 'cliente' ? 'Cliente y tipo' : step === 'items' ? 'Productos' : 'Resumen y envío'}
            </h2>
          </div>
          <button onClick={onClose} style={closeBtn}>×</button>
        </div>

        {/* Body con scroll */}
        <div style={{ padding: 28, overflowY: 'auto', flex: 1 }}>
          {step === 'cliente' && (
            <ClienteStep
              customers={customers}
              selected={selectedCustomer}
              onSelect={setSelectedCustomer}
              kind={kind}
              onKind={setKind}
              markupPct={markupPct}
              onMarkup={setMarkupPct}
            />
          )}

          {step === 'items' && (
            <ItemsStep
              productSearch={productSearch}
              onSearchChange={setProductSearch}
              matches={matches}
              onAddProduct={addProduct}
              items={items}
              onUpdate={updateItem}
              onRemove={removeItem}
              kind={kind}
              tierDiscount={tierDiscount}
              fmtUsd={fmtUsd}
              usdMxn={usdMxn}
              subtotal={subtotal}
            />
          )}

          {step === 'resumen' && (
            <ResumenStep
              customer={selectedCustomer}
              kind={kind}
              items={items}
              subtotal={subtotal}
              total={total}
              validUntil={validUntil}
              onValidUntil={setValidUntil}
              notes={notes}
              onNotes={setNotes}
              paymentTerms={paymentTerms}
              onPaymentTerms={setPaymentTerms}
              deliveryTerms={deliveryTerms}
              onDeliveryTerms={setDeliveryTerms}
              fmtUsd={fmtUsd}
            />
          )}
        </div>

        {err && (
          <div style={{ padding: '8px 28px', background: 'rgba(220,38,38,0.07)', borderTop: `1px solid ${theme.err}`, color: theme.err, fontSize: 13 }}>
            {err}
          </div>
        )}

        {/* Footer */}
        <div style={{ padding: '16px 28px', borderTop: `1px solid ${theme.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <div style={{ fontSize: 13, color: theme.textMuted }}>
            {items.length > 0 && step !== 'cliente' && (
              <>
                <strong style={{ color: theme.brand }}>{items.length}</strong> ítems · Subtotal{' '}
                <strong style={{ color: theme.brand }}>{fmtUsd(subtotal)}</strong>
              </>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {step !== 'cliente' && (
              <button
                onClick={() => setStep(step === 'resumen' ? 'items' : 'cliente')}
                style={btnSecondary}
              >
                ← Atrás
              </button>
            )}
            {step !== 'resumen' && (
              <button
                disabled={step === 'cliente' && !selectedCustomer}
                onClick={() => setStep(step === 'cliente' ? 'items' : 'resumen')}
                style={{
                  ...btnPrimary,
                  opacity: (step === 'cliente' && !selectedCustomer) ? 0.4 : 1,
                  cursor: (step === 'cliente' && !selectedCustomer) ? 'not-allowed' : 'pointer',
                }}
              >
                Siguiente →
              </button>
            )}
            {step === 'resumen' && (
              <>
                <button onClick={() => save('draft')} disabled={saving} style={btnSecondary}>
                  Guardar borrador
                </button>
                <button onClick={() => save('send')} disabled={saving} style={btnPrimary}>
                  {saving ? 'Enviando…' : 'Asignar y enviar →'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============== STEPS ==============

function ClienteStep({
  customers, selected, onSelect, kind, onKind, markupPct, onMarkup,
}: {
  customers: Customer[];
  selected: Customer | null;
  onSelect: (c: Customer) => void;
  kind: 'to_nuled' | 'to_client';
  onKind: (k: 'to_nuled' | 'to_client') => void;
  markupPct: number;
  onMarkup: (n: number) => void;
}) {
  const [q, setQ] = useState('');
  const filtered = customers.filter(c => c.business_name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 28 }}>
      <div>
        <Label>Cliente *</Label>
        <input
          placeholder="Buscar cliente…"
          value={q}
          onChange={e => setQ(e.target.value)}
          style={inputStyle}
        />
        <div style={{ marginTop: 10, maxHeight: 360, overflowY: 'auto', border: `1px solid ${theme.border}` }}>
          {filtered.map(c => (
            <button
              key={c.id}
              onClick={() => onSelect(c)}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: 12,
                background: selected?.id === c.id ? theme.bgFooter : theme.bg,
                borderLeft: selected?.id === c.id ? `4px solid ${theme.accent}` : '4px solid transparent',
                border: 'none',
                borderBottom: `1px solid ${theme.border}`,
                cursor: 'pointer',
                fontFamily: theme.font.sans,
              }}
            >
              <div style={{ fontWeight: 600, color: theme.brand, fontSize: 14 }}>{c.business_name}</div>
              <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 2 }}>
                {c.tier_name ?? '—'} · descuento {c.tier_discount_pct ?? 0}%
              </div>
            </button>
          ))}
          {filtered.length === 0 && (
            <div style={{ padding: 16, textAlign: 'center', color: theme.textMuted, fontSize: 13 }}>
              Sin coincidencias
            </div>
          )}
        </div>
      </div>

      <div>
        <Label>Tipo de cotización *</Label>
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <KindBtn active={kind === 'to_nuled'} onClick={() => onKind('to_nuled')}>
            <strong>A NULED (mayoreo)</strong>
            <div style={kindDesc}>El distribuidor compra a NULED · aplica su tier_discount</div>
          </KindBtn>
          <KindBtn active={kind === 'to_client'} onClick={() => onKind('to_client')}>
            <strong>Al cliente final</strong>
            <div style={kindDesc}>Cotización que el distribuidor le presenta a su cliente · con markup</div>
          </KindBtn>
        </div>

        {kind === 'to_client' && (
          <div>
            <Label>Markup % aplicado al PVP</Label>
            <input
              type="number"
              value={markupPct}
              onChange={e => onMarkup(Number(e.target.value))}
              style={inputStyle}
            />
            <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 4 }}>
              El precio de cada línea se calcula como costo distribuidor × (1 + markup/100)
            </div>
          </div>
        )}

        {selected && (
          <div style={{ marginTop: 16, padding: 14, background: theme.bgFooter, border: `1px solid ${theme.border}` }}>
            <div style={{ fontSize: 11, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>
              Cliente seleccionado
            </div>
            <div style={{ fontFamily: theme.font.head, fontSize: 18, color: theme.brand, marginTop: 4 }}>
              {selected.business_name}
            </div>
            <div style={{ fontSize: 13, color: theme.textMuted, marginTop: 4 }}>
              {selected.tier_name} · {selected.tier_discount_pct ?? 0}% de descuento sobre PVP
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ItemsStep({
  productSearch, onSearchChange, matches, onAddProduct,
  items, onUpdate, onRemove, kind, tierDiscount, fmtUsd, usdMxn, subtotal,
}: {
  productSearch: string;
  onSearchChange: (s: string) => void;
  matches: SearchOption[];
  onAddProduct: (p: SearchOption) => void;
  items: LineItem[];
  onUpdate: (i: number, patch: Partial<LineItem>) => void;
  onRemove: (i: number) => void;
  kind: 'to_nuled' | 'to_client';
  tierDiscount: number;
  fmtUsd: (n: number | null | undefined) => string;
  usdMxn: number | null;
  subtotal: number;
}) {
  return (
    <div>
      <div style={{ position: 'relative', marginBottom: 16 }}>
        <input
          placeholder="Buscar producto por SKU o nombre…"
          value={productSearch}
          onChange={e => onSearchChange(e.target.value)}
          style={{ ...inputStyle, fontSize: 14, padding: '12px 16px' }}
        />
        {matches.length > 0 && (
          <div style={{
            position: 'absolute', top: '100%', left: 0, right: 0,
            background: theme.bg, border: `1px solid ${theme.border}`,
            zIndex: 10, maxHeight: 280, overflowY: 'auto',
            boxShadow: '0 6px 14px rgba(0,0,0,0.08)',
          }}>
            {matches.map(p => (
              <button
                key={`${p.product_id}::${p.variant_id ?? 'canonical'}`}
                onClick={() => onAddProduct(p)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  width: '100%', padding: '10px 14px',
                  background: theme.bg, border: 'none',
                  borderBottom: `1px solid ${theme.border}`,
                  cursor: 'pointer', textAlign: 'left',
                  fontFamily: theme.font.sans,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: theme.brand, fontWeight: 500 }}>
                    {p.name}
                    {p.variant_label && (
                      <span style={{ marginLeft: 6, color: theme.accentDark, fontSize: 12 }}>
                        · {p.variant_label}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: theme.textMuted, fontFamily: theme.font.mono }}>
                    {p.sku} · {p.brand_code}
                  </div>
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color: theme.accentDark, marginLeft: 12 }}>
                  {fmtUsd(p.msrp_usd)}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: theme.bgSoft }}>
            <Th>SKU / Producto</Th>
            <Th style={{ textAlign: 'right', width: 90 }}>Qty</Th>
            <Th style={{ textAlign: 'right', width: 110 }}>P. Unit USD</Th>
            <Th style={{ textAlign: 'right', width: 80 }}>Desc %</Th>
            <Th style={{ textAlign: 'right', width: 130 }}>Total línea</Th>
            <Th style={{ width: 40 }}>{' '}</Th>
          </tr>
        </thead>
        <tbody>
          {items.map((it, i) => {
            const lineTotal = it.unit_price * it.quantity * (1 - it.discount_pct / 100);
            return (
              <tr key={i} style={{ borderBottom: `1px solid ${theme.border}` }}>
                <Td>
                  <div style={{ fontFamily: theme.font.mono, fontSize: 11, color: theme.textMuted }}>{it.sku}</div>
                  <div style={{ fontSize: 13, color: theme.brand, marginTop: 2 }}>{it.description}</div>
                </Td>
                <Td>
                  <input
                    type="number" min={1}
                    value={it.quantity}
                    onChange={e => onUpdate(i, { quantity: Math.max(1, Number(e.target.value)) })}
                    style={cellInput}
                  />
                </Td>
                <Td>
                  <input
                    type="number" step={0.01}
                    value={it.unit_price}
                    onChange={e => onUpdate(i, { unit_price: Number(e.target.value) })}
                    style={cellInput}
                  />
                </Td>
                <Td>
                  <input
                    type="number" min={0} max={100} step={0.5}
                    value={it.discount_pct}
                    onChange={e => onUpdate(i, { discount_pct: Number(e.target.value) })}
                    style={cellInput}
                  />
                </Td>
                <Td style={{ textAlign: 'right', fontWeight: 700, color: theme.brand }}>
                  {fmtUsd(lineTotal)}
                  {usdMxn && (
                    <div style={{ fontSize: 10, color: theme.textMuted, fontWeight: 500 }}>
                      ≈ ${(lineTotal * usdMxn).toLocaleString('es-MX', { maximumFractionDigits: 0 })} MXN
                    </div>
                  )}
                </Td>
                <Td>
                  <button onClick={() => onRemove(i)} style={{
                    background: 'transparent', border: 'none', color: theme.err,
                    cursor: 'pointer', fontSize: 16,
                  }}>×</button>
                </Td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {items.length === 0 && (
        <div style={{ padding: 40, textAlign: 'center', color: theme.textMuted, fontSize: 13, border: `1px dashed ${theme.border}` }}>
          Sin productos. Busca arriba para agregar.
        </div>
      )}

      {items.length > 0 && (
        <div style={{ marginTop: 16, padding: 14, background: theme.bgFooter, border: `1px solid ${theme.border}`, textAlign: 'right' }}>
          <span style={{ color: theme.textMuted, fontSize: 12, marginRight: 12 }}>
            {kind === 'to_nuled' && tierDiscount > 0 && `Descuento tier ${tierDiscount}% aplicado en cada línea`}
          </span>
          <span style={{ fontSize: 11, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700, marginRight: 8 }}>
            Subtotal:
          </span>
          <span style={{ fontFamily: theme.font.head, fontSize: 22, color: theme.brand, fontWeight: 700 }}>
            {fmtUsd(subtotal)}
          </span>
        </div>
      )}
    </div>
  );
}

function ResumenStep({
  customer, kind, items, subtotal, total, validUntil, onValidUntil,
  notes, onNotes, paymentTerms, onPaymentTerms, deliveryTerms, onDeliveryTerms, fmtUsd,
}: {
  customer: Customer | null;
  kind: 'to_nuled' | 'to_client';
  items: LineItem[];
  subtotal: number;
  total: number;
  validUntil: string;
  onValidUntil: (s: string) => void;
  notes: string;
  onNotes: (s: string) => void;
  paymentTerms: string;
  onPaymentTerms: (s: string) => void;
  deliveryTerms: string;
  onDeliveryTerms: (s: string) => void;
  fmtUsd: (n: number | null | undefined) => string;
}) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 28 }}>
      <div>
        <div style={{ padding: 14, border: `1px solid ${theme.border}`, marginBottom: 16 }}>
          <Label>Cliente</Label>
          <div style={{ color: theme.brand, fontWeight: 600 }}>{customer?.business_name}</div>
          <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 4 }}>
            {kind === 'to_nuled' ? 'Cotización mayoreo a NULED' : 'Cotización al cliente final'} ·{' '}
            {customer?.tier_name} · {customer?.tier_discount_pct ?? 0}% descuento
          </div>
        </div>

        <Label>Vence el</Label>
        <input type="date" value={validUntil} onChange={e => onValidUntil(e.target.value)} style={inputStyle} />

        <div style={{ marginTop: 16 }}>
          <Label>Términos de pago</Label>
          <input value={paymentTerms} onChange={e => onPaymentTerms(e.target.value)} style={inputStyle} />
        </div>

        <div style={{ marginTop: 16 }}>
          <Label>Términos de entrega</Label>
          <input value={deliveryTerms} onChange={e => onDeliveryTerms(e.target.value)} style={inputStyle} />
        </div>

        <div style={{ marginTop: 16 }}>
          <Label>Notas (visibles para el cliente)</Label>
          <textarea
            rows={4}
            value={notes}
            onChange={e => onNotes(e.target.value)}
            placeholder="Aclaraciones, condiciones especiales, contacto…"
            style={{ ...inputStyle, resize: 'vertical', fontFamily: theme.font.sans }}
          />
        </div>
      </div>

      <div>
        <Label>Resumen</Label>
        <div style={{ border: `1px solid ${theme.border}`, padding: 14 }}>
          <div style={{ fontSize: 12, color: theme.textMuted, marginBottom: 6 }}>{items.length} líneas</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: `1px solid ${theme.border}` }}>
            <span style={{ color: theme.textMuted }}>Subtotal</span>
            <strong style={{ color: theme.brand }}>{fmtUsd(subtotal)}</strong>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: `1px solid ${theme.border}` }}>
            <span style={{ color: theme.textMuted }}>IVA (al facturar)</span>
            <span style={{ color: theme.textMuted }}>—</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0 4px', borderTop: `2px solid ${theme.border}` }}>
            <span style={{ color: theme.brand, fontWeight: 700 }}>TOTAL USD</span>
            <span style={{ fontFamily: theme.font.head, fontSize: 22, color: theme.accentDark, fontWeight: 700 }}>
              {fmtUsd(total)}
            </span>
          </div>
        </div>

        <div style={{ marginTop: 16, padding: 12, background: theme.accentSoft, border: `1px solid ${theme.accent}`, fontSize: 12, color: theme.brand, lineHeight: 1.5 }}>
          <strong>Al hacer click en "Asignar y enviar":</strong>
          <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
            <li>La cotización aparecerá en el panel del cliente.</li>
            <li>Se notificará por email/WhatsApp (próximo iter).</li>
            <li>Status pasa a "Enviada". Cuando se confirme el pago, márcala "Pagada" (ahí defines moneda y TC).</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

// =================== HELPERS ===================

function StatusTag({ status }: { status: string }) {
  const map: Record<string, { color: string; label: string }> = {
    draft:    { color: theme.textMuted, label: 'Borrador' },
    sent:     { color: theme.warn,      label: 'Enviada' },
    viewed:   { color: theme.warn,      label: 'Enviada' },
    paid:     { color: theme.accentDark, label: 'Pagada' },
    lost:     { color: theme.err,       label: 'Perdida' },
    accepted: { color: theme.accentDark, label: 'Pagada' },
    rejected: { color: theme.err,       label: 'Perdida' },
    expired:  { color: theme.err,       label: 'Perdida' },
  };
  const m = map[status] ?? { color: theme.textMuted, label: status };
  return <Tag color={m.color}>{m.label}</Tag>;
}

function Th({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <th style={{
      textAlign: 'left',
      padding: '12px 14px',
      fontSize: 10,
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      color: theme.textMuted,
      borderBottom: `1px solid ${theme.border}`,
      ...style,
    }}>{children}</th>
  );
}

function Td({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return <td style={{ padding: '10px 14px', verticalAlign: 'top', ...style }}>{children}</td>;
}

function Tag({ children, color }: { children: React.ReactNode; color: string }) {
  return (
    <span style={{
      fontSize: 10, fontWeight: 700,
      padding: '2px 8px',
      color, border: `1px solid ${color}`,
      textTransform: 'uppercase', letterSpacing: 0.5,
      whiteSpace: 'nowrap',
    }}>{children}</span>
  );
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick} style={{
      background: 'transparent',
      color: active ? theme.brand : theme.textMuted,
      border: 'none',
      borderBottom: `2px solid ${active ? theme.accent : 'transparent'}`,
      padding: '12px 16px',
      fontSize: 12, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
      cursor: 'pointer', fontFamily: theme.font.sans, marginBottom: -1,
    }}>{children}</button>
  );
}

function Mini({ children }: { children: React.ReactNode }) {
  return <span style={{ opacity: 0.6, marginLeft: 4 }}>({children})</span>;
}

function KindBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick} style={{
      flex: 1,
      padding: 12,
      background: active ? theme.bgFooter : theme.bg,
      border: `1.5px solid ${active ? theme.accent : theme.border}`,
      cursor: 'pointer',
      textAlign: 'left',
      fontFamily: theme.font.sans,
      color: theme.brand,
      fontSize: 13,
    }}>{children}</button>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      fontSize: 11, color: theme.textMuted,
      textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700,
      marginBottom: 6,
    }}>{children}</div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  background: theme.bg,
  border: `1px solid ${theme.border}`,
  padding: '10px 12px',
  color: theme.text,
  fontSize: 13,
  fontFamily: theme.font.sans,
  outline: 'none',
  boxSizing: 'border-box',
};

const cellInput: React.CSSProperties = {
  width: '100%',
  background: theme.bg,
  border: `1px solid ${theme.border}`,
  padding: '6px 8px',
  color: theme.brand,
  fontSize: 13,
  fontFamily: theme.font.sans,
  outline: 'none',
  textAlign: 'right',
  boxSizing: 'border-box',
};

const btnPrimary: React.CSSProperties = {
  background: theme.accent,
  color: theme.brand,
  border: 'none',
  padding: '10px 18px',
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  cursor: 'pointer',
  fontFamily: theme.font.sans,
};

const pdfBtnStyle: React.CSSProperties = {
  display: 'inline-block',
  padding: '4px 8px',
  background: theme.bg,
  border: `1px solid ${theme.border}`,
  fontSize: 14,
  textDecoration: 'none',
  cursor: 'pointer',
  lineHeight: 1,
};

const btnSecondary: React.CSSProperties = {
  background: 'transparent',
  color: theme.brand,
  border: `1px solid ${theme.border}`,
  padding: '10px 18px',
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  cursor: 'pointer',
  fontFamily: theme.font.sans,
};

const closeBtn: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  fontSize: 28,
  color: theme.textMuted,
  cursor: 'pointer',
  padding: 0,
  lineHeight: 1,
};

const kindDesc: React.CSSProperties = {
  fontSize: 11,
  color: theme.textMuted,
  marginTop: 4,
  fontWeight: 400,
};

// =====================================================================
// QUOTE DETAIL — modal de detalle / acciones sobre una cotización
// =====================================================================

type QuoteFull = {
  id: string;
  quote_number: string;
  customer_id: string | null;
  kind: 'to_nuled' | 'to_client';
  status: string;
  currency: string;
  subtotal: number | null;
  total: number | null;
  tax_amount: number | null;
  discount_amount: number | null;
  shipping_amount: number | null;
  markup_pct: number | null;
  valid_until: string | null;
  sent_at: string | null;
  accepted_at: string | null;
  payment_terms: string | null;
  delivery_terms: string | null;
  notes: string | null;
  client_name: string | null;
  client_contact: string | null;
  created_at: string;
  customers?: { business_name: string; primary_email: string | null } | null;
};

type QuoteItem = {
  id: string;
  sku: string;
  brand_code: string;
  description: string;
  quantity: number;
  unit: string | null;
  unit_price: number;
  discount_pct: number;
  line_total: number;
  sort_order: number;
};

const STATUSES: Array<{ value: string; label: string }> = [
  { value: 'draft', label: 'Borrador' },
  { value: 'sent',  label: 'Enviada' },
  { value: 'paid',  label: 'Pagada' },
  { value: 'lost',  label: 'Perdida' },
];

function QuoteDetail({ quoteId, onClose, onChanged, onEdit, onNewVersion, onOpenVersion }: {
  quoteId: string;
  onClose: () => void;
  onChanged: () => void;
  onEdit: (id: string) => void;
  onNewVersion: (id: string) => void;
  onOpenVersion: (id: string) => void;
}) {
  const { fmtUsd, usdMxn } = useCurrency();
  const [quote, setQuote] = useState<QuoteFull | null>(null);
  const [items, setItems] = useState<QuoteItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [versions, setVersions] = useState<{ id: string; version: number; status: string; is_current: boolean }[]>([]);
  const [payModal, setPayModal] = useState(false);
  const [payCur, setPayCur] = useState<'USD' | 'MXN'>('USD');
  const [payTc, setPayTc] = useState('');

  useEffect(() => {
    (async () => {
      setLoading(true);
      const { data: q } = await supabase
        .from('quotes')
        .select('*, customers(business_name, primary_email)')
        .eq('id', quoteId)
        .maybeSingle();
      const { data: its } = await supabase
        .from('quote_items')
        .select('*')
        .eq('quote_id', quoteId)
        .order('sort_order');
      setQuote(q as QuoteFull);
      {
        const root = (q as any)?.root_quote_id ?? (q as any)?.id;
        if (root) {
          supabase.from('quotes').select('id, version, status, is_current').eq('root_quote_id', root).order('version')
            .then(({ data }) => setVersions((data as any) ?? []));
        }
        // ETA automática si no se ha ajustado a mano
        if (!(q as any).eta_manual) {
          supabase.rpc('estimate_quote_ready', { p_quote_id: (q as any).id }).then(({ data: d }) => {
            if (d) {
              supabase.from('quotes').update({ estimated_ready_date: d }).eq('id', (q as any).id).then(() => {});
              setQuote(prev => prev ? ({ ...prev, estimated_ready_date: d } as any) : prev);
            }
          });
        }
      }
      setItems((its as QuoteItem[]) ?? []);
      setLoading(false);
    })();
  }, [quoteId]);

  async function changeStatus(newStatus: string) {
    if (!quote) return;
    // Al marcar "Pagada" abrimos el modal para elegir moneda de pago y TC (botones, no texto)
    if (newStatus === 'paid') {
      setPayCur(((quote as any).paid_currency as 'USD' | 'MXN') || 'USD');
      setPayTc((quote as any).tipo_cambio_cierre ? String((quote as any).tipo_cambio_cierre) : '');
      setPayModal(true);
      return;
    }
    const patch: any = { status: newStatus };
    if (newStatus === 'sent' && !quote.sent_at) patch.sent_at = new Date().toISOString();
    setUpdating(true);
    const { error } = await supabase.from('quotes').update(patch).eq('id', quoteId);
    setUpdating(false);
    if (error) { alert(error.message); return; }
    setQuote(prev => prev ? { ...prev, ...patch } : prev);
    onChanged();
  }

  async function confirmPago() {
    if (!quote) return;
    const patch: any = { status: 'paid', paid_currency: payCur };
    if (payCur === 'MXN') {
      const v = Number(payTc);
      if (payTc.trim() === '' || isNaN(v) || v <= 0) { alert('Captura un tipo de cambio válido.'); return; }
      patch.tipo_cambio_cierre = v;
    } else {
      patch.tipo_cambio_cierre = null;
    }
    setUpdating(true);
    const { error } = await supabase.from('quotes').update(patch).eq('id', quoteId);
    setUpdating(false);
    if (error) { alert(error.message); return; }
    setQuote(prev => prev ? { ...prev, ...patch } : prev);
    setPayModal(false);
    onChanged();
  }

  async function saveEta(date: string) {
    if (!quote) return;
    const val = date || null;
    const { error } = await supabase.from('quotes').update({ estimated_ready_date: val, eta_manual: true }).eq('id', quoteId);
    if (error) { alert(error.message); return; }
    setQuote(prev => prev ? ({ ...prev, estimated_ready_date: val, eta_manual: true } as any) : prev);
    onChanged();
  }
  async function recalcEta() {
    if (!quote) return;
    const { data: d } = await supabase.rpc('estimate_quote_ready', { p_quote_id: quoteId });
    const { error } = await supabase.from('quotes').update({ estimated_ready_date: d ?? null, eta_manual: false }).eq('id', quoteId);
    if (error) { alert(error.message); return; }
    setQuote(prev => prev ? ({ ...prev, estimated_ready_date: d ?? null, eta_manual: false } as any) : prev);
    onChanged();
  }

  async function deleteQuote() {
    if (!confirm(`¿Eliminar la cotización ${quote?.quote_number}? Esta acción no se puede deshacer.`)) return;
    const { error } = await supabase.from('quotes').delete().eq('id', quoteId);
    if (error) { alert(error.message); return; }
    onChanged();
    onClose();
  }

  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: 'rgba(0,0,0,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: 16,
    }}>
      <div style={{
        background: theme.bg,
        border: `1px solid ${theme.border}`,
        width: '100%', maxWidth: 1100,
        maxHeight: '94vh', display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 28px', borderBottom: `1px solid ${theme.border}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16,
        }}>
          <div>
            <div style={{ fontSize: 11, color: theme.accentDark, letterSpacing: 1.5, textTransform: 'uppercase', fontWeight: 700 }}>
              Cotización {quote?.quote_number ?? '…'}
            </div>
            <h2 style={{ fontFamily: theme.font.head, fontSize: 22, fontWeight: 400, color: theme.brand, margin: '4px 0 0' }}>
              {quote?.customers?.business_name ?? quote?.client_name ?? '—'}
            </h2>
            {versions.length > 1 && (
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 10, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 10, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>Versiones:</span>
                {versions.map(v => {
                  const active = v.id === quoteId;
                  return (
                    <button key={v.id} type="button" onClick={() => { if (!active) onOpenVersion(v.id); }}
                      title={v.is_current ? 'Versión vigente' : 'Ver esta versión'}
                      style={{
                        padding: '3px 10px', fontSize: 12, fontWeight: 700, cursor: active ? 'default' : 'pointer', fontFamily: theme.font.sans,
                        border: `1px solid ${active ? theme.accent : theme.border}`,
                        background: active ? theme.accentSoft : 'transparent',
                        color: theme.brand,
                      }}>
                      v{v.version}{v.is_current ? ' ★' : ''}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <button onClick={onClose} style={closeBtn}>×</button>
        </div>

        {/* Body */}
        <div style={{ padding: 28, overflowY: 'auto', flex: 1 }}>
          {loading || !quote ? (
            <div style={{ padding: 40, textAlign: 'center', color: theme.textMuted }}>Cargando…</div>
          ) : (
            <>
              {/* Meta + status */}
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16,
                marginBottom: 20, padding: 16, background: theme.bgSoft, border: `1px solid ${theme.border}`,
              }}>
                <Meta label="Tipo" value={quote.kind === 'to_nuled' ? 'A NULED (mayoreo)' : 'AL CLIENTE final'} />
                <Meta label="Status" value={
                  <select
                    value={quote.status}
                    disabled={updating}
                    onChange={e => changeStatus(e.target.value)}
                    style={{
                      ...inputStyle, padding: '6px 10px', fontSize: 12,
                      cursor: updating ? 'wait' : 'pointer', width: 'auto',
                    }}
                  >
                    {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                } />
                {(quote as any).paid_currency && (
                  <Meta label="Pagado en" value={(quote as any).paid_currency} />
                )}
                {(quote as any).paid_currency === 'MXN' && (
                  <Meta label="Tipo de cambio (pago)" value={(quote as any).tipo_cambio_cierre != null ? `$${(quote as any).tipo_cambio_cierre} MXN/USD` : '—'} />
                )}
                <Meta label="Vence" value={quote.valid_until ?? '—'} />
                <Meta label="Entrega estimada" value={
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                    <input type="date" value={(quote as any).estimated_ready_date ?? ''} onChange={e => saveEta(e.target.value)}
                      style={{ ...inputStyle, padding: '6px 10px', fontSize: 12, width: 'auto' }} />
                    {(quote as any).eta_manual
                      ? <button type="button" onClick={recalcEta} title="Volver a cálculo automático" style={{ background: 'transparent', border: 'none', color: theme.accentDark, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>↻ auto</button>
                      : <span style={{ fontSize: 10, color: theme.textMuted }}>auto</span>}
                  </div>
                } />
                <Meta label="Creada" value={new Date(quote.created_at).toLocaleDateString('es-MX')} />
                {quote.sent_at && <Meta label="Enviada" value={new Date(quote.sent_at).toLocaleDateString('es-MX')} />}
                {quote.accepted_at && <Meta label="Aceptada" value={new Date(quote.accepted_at).toLocaleDateString('es-MX')} />}
                {quote.markup_pct != null && <Meta label="Margen aplicado" value={`${quote.markup_pct}%`} />}
                {quote.customers?.primary_email && <Meta label="Email cliente" value={quote.customers.primary_email} />}
              </div>

              {/* Items */}
              <h3 style={{ fontFamily: theme.font.head, fontSize: 14, fontWeight: 700, color: theme.brand, margin: '0 0 10px', textTransform: 'uppercase', letterSpacing: 0.6 }}>
                Productos
              </h3>
              <div style={{ background: theme.bg, border: `1px solid ${theme.border}`, overflowX: 'auto', marginBottom: 20 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 700 }}>
                  <thead>
                    <tr style={{ background: theme.bgSoft }}>
                      <Th>SKU / Producto</Th>
                      <Th style={{ textAlign: 'right', width: 80 }}>Qty</Th>
                      <Th style={{ textAlign: 'right', width: 110 }}>Unit USD</Th>
                      <Th style={{ textAlign: 'right', width: 80 }}>Desc %</Th>
                      <Th style={{ textAlign: 'right', width: 130 }}>Total línea</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map(it => (
                      <tr key={it.id} style={{ borderBottom: `1px solid ${theme.border}` }}>
                        <Td>
                          <div style={{ fontFamily: theme.font.mono, fontSize: 11, color: theme.textMuted }}>
                            {it.sku} · {it.brand_code}
                          </div>
                          <div style={{ fontSize: 13, color: theme.brand, marginTop: 2 }}>{it.description}</div>
                        </Td>
                        <Td style={{ textAlign: 'right' }}>{it.quantity}</Td>
                        <Td style={{ textAlign: 'right' }}>{fmtUsd(it.unit_price)}</Td>
                        <Td style={{ textAlign: 'right' }}>{it.discount_pct}%</Td>
                        <Td style={{ textAlign: 'right', fontWeight: 700, color: theme.brand }}>
                          {fmtUsd(it.line_total)}
                          {usdMxn && (
                            <div style={{ fontSize: 10, color: theme.textMuted, fontWeight: 500 }}>
                              ≈ ${(it.line_total * usdMxn).toLocaleString('es-MX', { maximumFractionDigits: 0 })} MXN
                            </div>
                          )}
                        </Td>
                      </tr>
                    ))}
                    {items.length === 0 && (
                      <tr><td colSpan={5} style={{ padding: 24, textAlign: 'center', color: theme.textMuted }}>
                        Esta cotización no tiene items.
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Totales */}
              <div style={{
                display: 'flex', justifyContent: 'flex-end', marginBottom: 20,
              }}>
                <div style={{
                  background: theme.bgSoft, border: `1px solid ${theme.border}`,
                  padding: 16, minWidth: 280,
                }}>
                  <TotalRow label="Subtotal" value={fmtUsd(quote.subtotal)} />
                  {quote.discount_amount ? <TotalRow label="Descuento" value={`− ${fmtUsd(quote.discount_amount)}`} /> : null}
                  {quote.shipping_amount ? <TotalRow label="Envío" value={fmtUsd(quote.shipping_amount)} /> : null}
                  {quote.tax_amount ? <TotalRow label="IVA" value={fmtUsd(quote.tax_amount)} /> : null}
                  <div style={{ borderTop: `1px solid ${theme.border}`, marginTop: 8, paddingTop: 8 }}>
                    <TotalRow label="Total" value={fmtUsd(quote.total)} bold />
                    {usdMxn && quote.total && (
                      <div style={{ fontSize: 11, color: theme.textMuted, textAlign: 'right', marginTop: 2 }}>
                        ≈ ${(Number(quote.total) * usdMxn).toLocaleString('es-MX', { maximumFractionDigits: 0 })} MXN
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Términos y notas */}
              {(quote.payment_terms || quote.delivery_terms || quote.notes) && (
                <div style={{ marginBottom: 20, padding: 14, background: theme.bgSoft, border: `1px solid ${theme.border}` }}>
                  {quote.payment_terms && <TermRow label="Pago" value={quote.payment_terms} />}
                  {quote.delivery_terms && <TermRow label="Entrega" value={quote.delivery_terms} />}
                  {quote.notes && <TermRow label="Notas" value={quote.notes} />}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer con acciones */}
        {!loading && quote && (
          <div style={{
            padding: '14px 28px', borderTop: `1px solid ${theme.border}`,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap',
          }}>
            <button onClick={deleteQuote} style={{
              background: 'transparent', color: theme.err, border: `1px solid ${theme.err}`,
              padding: '8px 14px', fontSize: 11, fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: 0.5, cursor: 'pointer',
              fontFamily: theme.font.sans,
            }}>
              Eliminar
            </button>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              {(quote as any).version > 1 && (
                <span style={{ fontSize: 11, fontWeight: 700, color: theme.accentDark, padding: '4px 8px', background: theme.accentSoft, border: `1px solid ${theme.accent}` }}>
                  v{(quote as any).version}
                </span>
              )}
              {quote.status === 'draft' && (
                <button onClick={() => onEdit(quote.id)} style={btnSecondaryAnchor as any}>✏️ Editar</button>
              )}
              <button onClick={() => onNewVersion(quote.id)} style={btnSecondaryAnchor as any} title="Duplica esta cotización como una nueva versión editable (ej. de 14 a 20 piezas)">🔁 Nueva versión</button>
              <a
                href={`/cotizacion/${quote.id}/pdf?modo=interno`}
                target="_blank" rel="noreferrer"
                style={btnSecondaryAnchor}
              >
                📋 PDF interno (con margen)
              </a>
              <a
                href={`/cotizacion/${quote.id}/pdf`}
                target="_blank" rel="noreferrer"
                style={btnPrimary}
              >
                📄 PDF cliente
              </a>
            </div>
          </div>
        )}
      </div>
      {payModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: 16 }}>
          <div style={{ background: theme.bg, border: `1px solid ${theme.border}`, width: '100%', maxWidth: 420, padding: 24, position: 'relative' }}>
            <button type="button" onClick={() => setPayModal(false)} aria-label="Cerrar" style={{ position: 'absolute', top: 10, right: 14, background: 'transparent', border: 'none', color: theme.textMuted, fontSize: 26, lineHeight: 1, cursor: 'pointer', padding: 4 }}>×</button>
            <div style={{ fontSize: 11, color: theme.accentDark, letterSpacing: 1.5, textTransform: 'uppercase', fontWeight: 700 }}>Registrar pago</div>
            <h2 style={{ fontFamily: theme.font.head, fontSize: 20, fontWeight: 400, color: theme.brand, margin: '4px 0 14px' }}>¿En qué moneda se pagó?</h2>
            <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
              {(['USD', 'MXN'] as const).map(c => (
                <button key={c} type="button" onClick={() => setPayCur(c)}
                  style={{
                    flex: 1, padding: '14px 0', fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: theme.font.sans,
                    border: `2px solid ${payCur === c ? theme.accent : theme.border}`,
                    background: payCur === c ? theme.accentSoft : 'transparent',
                    color: theme.brand,
                  }}>{c}</button>
              ))}
            </div>
            {payCur === 'MXN' && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, color: theme.textMuted, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>Tipo de cambio (MXN por USD)</div>
                <input type="number" step="0.0001" value={payTc} onChange={e => setPayTc(e.target.value)} autoFocus
                  placeholder="Ej: 17.50" style={{ ...inputStyle }} />
                <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 4 }}>Queda fijo para pedidos y facturación.</div>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
              <button type="button" onClick={() => setPayModal(false)} style={btnSecondary}>Cancelar</button>
              <button type="button" onClick={confirmPago} disabled={updating}
                style={{ ...btnPrimary, opacity: updating ? 0.6 : 1 }}>
                {updating ? 'Guardando…' : 'Marcar como Pagada'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Meta({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700, marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: 13, color: theme.brand, fontWeight: 500 }}>{value}</div>
    </div>
  );
}

function TotalRow({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: bold ? 15 : 13, fontWeight: bold ? 700 : 500, color: theme.brand }}>
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}

function TermRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', gap: 10, marginBottom: 6, fontSize: 12, lineHeight: 1.5 }}>
      <span style={{ minWidth: 70, color: theme.textMuted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 11 }}>
        {label}
      </span>
      <span style={{ flex: 1, color: theme.brand }}>{value}</span>
    </div>
  );
}

const btnSecondaryAnchor: React.CSSProperties = {
  background: theme.bg, color: theme.brand,
  border: `1px solid ${theme.borderStrong}`,
  padding: '10px 16px', fontSize: 11, fontWeight: 700,
  textTransform: 'uppercase', letterSpacing: 0.5, cursor: 'pointer',
  fontFamily: theme.font.sans, textDecoration: 'none',
  display: 'inline-block',
};
